import { useState, useEffect, useRef, useCallback } from 'react';
import type {
  ServerEvent,
  ClientEvent,
  ServerEventTypeValue,
} from '../types/events';
import { ClientEventType } from '../types/events';

/**
 * localStorage key prefix holding TOOL_RESPONSE submissions made while the
 * socket was down. Persisted so a filled-in form survives a full page reload
 * (e.g. mobile tab suspend that unloads the page) and is replayed on the next
 * connection instead of being silently lost (CAIV-440). Scoped per
 * channel/router so it can't leak across widgets — see pendingStorageKey.
 */
const PENDING_TOOL_RESPONSES_PREFIX = 'cp_widget_pending_tool_responses';
// Drop persisted submissions older than this on load, so a user returning much
// later doesn't replay a stale answer (CAIV-440). This is a UX bound, not a
// technical one: the backend accepts a late TOOL_RESPONSE regardless of age —
// it resumes the parked Foundry run if still live (Azure expires a run 10 min
// after creation, total elapsed time), else replays the selection as a fresh
// user turn on the same thread (CAIV-405). 1h keeps replay to a coherent
// same-session return; beyond that the user is better served re-engaging fresh.
const PENDING_TOOL_RESPONSE_TTL_MS = 60 * 60 * 1000;
// Minimum gap between proactive (visibility/online) reconnect attempts, so a
// flapping network can't drive a tight reconnect loop (CAIV-440 review).
const MIN_RECONNECT_INTERVAL_MS = 3000;
/**
 * Reconnect backoff, the same policy as the inbox socket (CAIV-1366). Attempt n
 * waits `min(base * 2^n, cap)` stretched by a random factor between 1 and 2:
 * never shorter than before, and visitors dropped by the same pod restart
 * spread out. Once the chat has connected it retries for as long as it is
 * open: one attempt per 30-60 s visible, per 2-4 minutes hidden.
 */
const MAX_RECONNECT_DELAY_MS = 30_000;
const HIDDEN_MAX_RECONNECT_DELAY_MS = 120_000;
/**
 * The attempt count resets only once a connection has held this long, so a
 * server that accepts and then drops straight away is still backed off.
 */
const STABLE_CONNECTION_MS = 10_000;
/**
 * The pong watchdog, as in the inbox (CAIV-1366). A socket can stay open while
 * nothing comes back (a phone that slept, a network that switched, a server
 * that froze), and the chat would look connected while every reply is lost. A
 * ping left unanswered for one ping interval gets one more ping and this long,
 * then the socket is given up and reconnected. Measured as the wall-clock age
 * of that ping, never time since the last pong: a hidden tab whose timers run
 * once a minute hears its pongs a minute apart and is still healthy.
 */
const DEAF_CONFIRM_MS = 5_000;

/**
 * Scope the persisted-submissions key by channel/router so an answer stored for
 * one widget can never be replayed into a different widget/conversation sharing
 * the same origin (CAIV-440 review).
 */
function pendingStorageKey(channelId: string | undefined, routerId: string): string {
  return `${PENDING_TOOL_RESPONSES_PREFIX}:${channelId || routerId}`;
}

interface PersistedToolResponse {
  event: ClientEvent;
  ts: number;
}

function getToolCallId(event: ClientEvent): string | undefined {
  return event.type === ClientEventType.TOOL_RESPONSE ? event.tool_call_id : undefined;
}

function loadPersistedRaw(key: string): PersistedToolResponse[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const now = Date.now();
    return (parsed as PersistedToolResponse[]).filter(
      (e) =>
        e &&
        e.event &&
        typeof e.ts === 'number' &&
        now - e.ts < PENDING_TOOL_RESPONSE_TTL_MS
    );
  } catch {
    return [];
  }
}

function loadPendingToolResponses(key: string): ClientEvent[] {
  return loadPersistedRaw(key).map((e) => e.event);
}

function persistPendingToolResponse(key: string, event: ClientEvent): void {
  if (event.type !== ClientEventType.TOOL_RESPONSE) return;
  try {
    const others = loadPersistedRaw(key).filter(
      (e) => getToolCallId(e.event) !== event.tool_call_id
    );
    others.push({ event, ts: Date.now() });
    localStorage.setItem(key, JSON.stringify(others));
  } catch {
    // localStorage unavailable (private mode / blocked in an embed). Best
    // effort — the in-memory queue still covers same-session reconnects.
  }
}

function clearPendingToolResponses(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

/**
 * WebSocket connection status.
 */
export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'reconnecting' | 'unavailable';

/**
 * Event handler type for server events.
 */
export type ServerEventHandler<T extends ServerEvent = ServerEvent> = (event: T) => void;

/**
 * Options for the useWebSocket hook.
 */
export interface UseWebSocketOptions {
  /** WebSocket host URL (e.g., 'ws://localhost:8000') */
  wsHost: string;
  /** Router ID to connect to (legacy — use channelId when available) */
  routerId: string;
  /** Channel ID (UUID) — preferred, connects via /ws/channel/{channelId}/ */
  channelId?: string;
  /** Session ID for restoration (optional) */
  sessionId?: string | null;
  /** Whether to automatically connect on mount */
  autoConnect?: boolean;
  /** Ping interval in milliseconds (default: 30000) */
  pingInterval?: number;
  /**
   * Reconnection attempts for a chat that has never connected (default: 5).
   * A chat that was connected keeps retrying (CAIV-1366).
   */
  maxReconnectAttempts?: number;
  /** Base delay for reconnection in milliseconds (default: 1000) */
  reconnectBaseDelay?: number;
}

/**
 * Return type for the useWebSocket hook.
 */
export interface UseWebSocketReturn {
  /** Current connection status */
  status: ConnectionStatus;
  /** Whether the WebSocket is connected */
  isConnected: boolean;
  /** Send a client event to the server */
  send: (event: ClientEvent) => void;
  /** Send a user message */
  sendMessage: (message: string) => void;
  /** Manually connect to the WebSocket */
  connect: () => void;
  /** Manually disconnect from the WebSocket */
  disconnect: () => void;
  /** Register an event handler for a specific event type */
  on: <T extends ServerEvent>(
    eventType: ServerEventTypeValue,
    handler: ServerEventHandler<T>
  ) => () => void;
  /** Last error that occurred */
  error: string | null;
}

/**
 * Hook for managing WebSocket connection to the chat server.
 *
 * Handles connection, automatic reconnection with exponential backoff,
 * ping/pong keepalive, and event dispatching to registered handlers.
 *
 * @param options - WebSocket configuration options
 * @returns WebSocket connection state and methods
 */
export function useWebSocket(options: UseWebSocketOptions): UseWebSocketReturn {
  const {
    wsHost,
    routerId,
    channelId,
    sessionId,
    autoConnect = true,
    pingInterval = 30000,
    maxReconnectAttempts = 5,
    reconnectBaseDelay = 1000,
  } = options;

  const [status, setStatus] = useState<ConnectionStatus>('disconnected');
  const [error, setError] = useState<string | null>(null);

  // Refs for WebSocket and reconnection state
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stableTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The pong watchdog (see `DEAF_CONFIRM_MS`): when the oldest unanswered ping
  // went out, wall clock, and the timer that checks on it.
  const pingSentAtRef = useRef<number | null>(null);
  const pongTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const confirmingDeafRef = useRef(false);
  const probeRef = useRef<(ws: WebSocket) => void>(() => {});
  const isIntentionalCloseRef = useRef(false);
  const handlersRef = useRef<Map<ServerEventTypeValue, Set<ServerEventHandler>>>(new Map());
  const connectRef = useRef<() => void>(() => {});
  const disconnectRef = useRef<() => void>(() => {});
  const statusRef = useRef<ConnectionStatus>('disconnected');
  const sessionIdRef = useRef<string | null | undefined>(sessionId);
  // Events sent while the socket is not OPEN are buffered here and flushed on
  // the next successful open, so a transient disconnect can't lose a form
  // submission (CAIV-440).
  const outboundQueueRef = useRef<ClientEvent[]>([]);
  // Timestamp of the last proactive reconnect kick (visibility/online), used to
  // throttle them so flapping connectivity can't hammer a dead server.
  const lastReconnectKickRef = useRef(0);
  // Whether a socket has ever reached OPEN. It separates "this visitor's chat
  // dropped" from "this chat has never worked here", which want different
  // endings once the retries run out (CAIV-1041).
  const hasEverConnectedRef = useRef(false);

  /**
   * Reconnection delay: capped exponential backoff, jittered (see
   * `MAX_RECONNECT_DELAY_MS`). A hidden tab waits longer; showing it again
   * reconnects at once.
   */
  const getReconnectDelay = useCallback(
    (attempt: number): number => {
      const cap =
        document.visibilityState === 'hidden'
          ? HIDDEN_MAX_RECONNECT_DELAY_MS
          : MAX_RECONNECT_DELAY_MS;
      const floor = Math.min(reconnectBaseDelay * 2 ** Math.min(attempt, 20), cap);
      return floor * (1 + Math.random());
    },
    [reconnectBaseDelay]
  );

  /**
   * Clear all timers and intervals.
   */
  const clearTimers = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (stableTimeoutRef.current) {
      clearTimeout(stableTimeoutRef.current);
      stableTimeoutRef.current = null;
    }
    if (pongTimeoutRef.current) {
      clearTimeout(pongTimeoutRef.current);
      pongTimeoutRef.current = null;
    }
    pingSentAtRef.current = null;
    confirmingDeafRef.current = false;
  }, []);

  /**
   * Start the ping keepalive interval. Every tick goes through the watchdog.
   */
  const startPingInterval = useCallback((ws: WebSocket) => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }

    pingIntervalRef.current = setInterval(() => {
      probeRef.current(ws);
    }, pingInterval);
  }, [pingInterval]);

  /**
   * Dispatch a server event to registered handlers.
   */
  const dispatchEvent = useCallback((event: ServerEvent) => {
    const handlers = handlersRef.current.get(event.type);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(event);
        } catch (err) {
          console.error('[ClubPlannerWidget] Error in event handler:', err);
        }
      });
    }
  }, []);

  /**
   * Connect to the WebSocket server.
   */
  const connect = useCallback(() => {
    // Don't connect if already connected or connecting
    if (wsRef.current?.readyState === WebSocket.OPEN ||
        wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    setStatus('connecting');
    setError(null);
    isIntentionalCloseRef.current = false;

    // Build WebSocket URL — prefer channel-based endpoint
    const wsUrl = channelId
      ? `${wsHost}/ws/channel/${channelId}/`
      : `${wsHost}/ws/router/${routerId}/`;

    try {
      const ws = new WebSocket(wsUrl);

      // A socket already CLOSED before the constructor returns was refused by
      // the browser itself, not by the network: a host page whose CSP has no
      // `connect-src` admitting `wss:` (an `https:` source does not match a
      // `wss:` URL under CSP L3), or a mixed-content block. Such a socket
      // fires `error` and, as measured in Chromium, *never* `close`, so
      // `onclose`, our only route to a terminal state or a retry, never runs
      // and the widget would sit on 'connecting' forever behind a disabled
      // input with nothing to tell the visitor. Retrying cannot help either:
      // the same policy rejects every attempt for the life of the page. Go
      // straight to the terminal state so the unavailable panel renders
      // (CAIV-1041).
      if (ws.readyState === WebSocket.CLOSED) {
        setStatus('unavailable');
        setError('Connection blocked by the page (content security policy or mixed content)');
        return;
      }

      wsRef.current = ws;

      ws.onopen = () => {
        setStatus('connected');
        setError(null);
        hasEverConnectedRef.current = true;
        pingSentAtRef.current = null;
        startPingInterval(ws);
        stableTimeoutRef.current = setTimeout(() => {
          reconnectAttemptsRef.current = 0;
        }, STABLE_CONNECTION_MS);

        // If we have a session ID, attempt to restore it
        const restorationSessionId = sessionIdRef.current;
        if (restorationSessionId) {
          const restoreEvent: ClientEvent = {
            type: ClientEventType.RESTORE_SESSION,
            session_id: restorationSessionId,
          };
          ws.send(JSON.stringify(restoreEvent));
        }

        // Flush events queued while the socket was down (e.g. a form
        // submission made during a reconnect) so they are never lost
        // (CAIV-440). Sent after RESTORE_SESSION so the server has restored
        // the conversation context first. The in-memory queue covers
        // same-session reconnects; persisted TOOL_RESPONSEs cover a full page
        // reload. Dedupe by tool_call_id so a submission isn't sent twice.
        const storageKey = pendingStorageKey(channelId, routerId);
        const inMemory = outboundQueueRef.current;
        outboundQueueRef.current = [];
        const seenToolCallIds = new Set(
          inMemory
            .filter((e) => e.type === ClientEventType.TOOL_RESPONSE)
            .map((e) => (e as { tool_call_id: string }).tool_call_id)
        );
        const persisted = loadPendingToolResponses(storageKey).filter(
          (e) =>
            e.type !== ClientEventType.TOOL_RESPONSE ||
            !seenToolCallIds.has((e as { tool_call_id: string }).tool_call_id)
        );
        for (const queuedEvent of [...inMemory, ...persisted]) {
          ws.send(JSON.stringify(queuedEvent));
        }
        clearPendingToolResponses(storageKey);
      };

      ws.onmessage = (event) => {
        if (wsRef.current !== ws) return;
        // Anything from the server answers the watchdog.
        pingSentAtRef.current = null;
        confirmingDeafRef.current = false;
        if (pongTimeoutRef.current) {
          clearTimeout(pongTimeoutRef.current);
          pongTimeoutRef.current = null;
        }
        try {
          const serverEvent = JSON.parse(event.data) as ServerEvent;
          dispatchEvent(serverEvent);
        } catch (err) {
          console.error('[ClubPlannerWidget] Failed to parse WebSocket message:', err);
        }
      };

      ws.onerror = () => {
        setError('WebSocket connection error');
      };

      ws.onclose = (event) => {
        // Only the current socket counts: one the watchdog gave up on, or one
        // a scope change replaced, reports its close after the next exists.
        if (wsRef.current !== ws) return;
        clearTimers();
        wsRef.current = null;

        // Terminal: router not serving (4003) or not found (4004) — do not retry.
        if (event.code === 4003 || event.code === 4004) {
          setStatus('unavailable');
          return;
        }

        // Don't reconnect if intentionally closed or max attempts reached
        if (isIntentionalCloseRef.current) {
          setStatus('disconnected');
          return;
        }

        // A chat that has connected keeps trying while it is open; one that
        // never has gets `maxReconnectAttempts` (CAIV-1041, CAIV-1366).
        if (
          hasEverConnectedRef.current ||
          reconnectAttemptsRef.current < maxReconnectAttempts
        ) {
          setStatus('reconnecting');
          const delay = getReconnectDelay(reconnectAttemptsRef.current);
          reconnectAttemptsRef.current += 1;

          reconnectTimeoutRef.current = setTimeout(() => {
            connectRef.current();
          }, delay);
        } else {
          // Never reached OPEN in any attempt, so there is no connection to
          // regain and nothing the visitor can do by waiting. 'disconnected'
          // renders a dead input with no explanation; the terminal state at
          // least tells them the chat is not working (CAIV-1041).
          setStatus('unavailable');
          setError(`Connection failed. Code: ${event.code}`);
        }
      };
    } catch (err) {
      setStatus('disconnected');
      setError(err instanceof Error ? err.message : 'Failed to create WebSocket');
    }
  }, [wsHost, routerId, channelId, maxReconnectAttempts, getReconnectDelay, clearTimers, startPingInterval, dispatchEvent]);

  /**
   * Send a ping and watch for its answer (see `DEAF_CONFIRM_MS`). A ping already
   * unanswered for a full interval is not waited on again: one more ping, and a
   * few seconds for anything still queued, then the socket is given up as a
   * drop (it is detached first: a close against a server that does not answer
   * may never complete).
   */
  const probe = useCallback((ws: WebSocket) => {
    if (wsRef.current !== ws || ws.readyState !== WebSocket.OPEN) return;
    const sendPing = () => {
      const pingEvent: ClientEvent = {
        type: ClientEventType.PING,
        timestamp: Date.now(),
      };
      ws.send(JSON.stringify(pingEvent));
    };

    const sentAt = pingSentAtRef.current;
    if (sentAt !== null && Date.now() - sentAt >= pingInterval) {
      if (confirmingDeafRef.current) return;
      confirmingDeafRef.current = true;
      if (pongTimeoutRef.current) clearTimeout(pongTimeoutRef.current);
      sendPing();
      pongTimeoutRef.current = setTimeout(() => {
        pongTimeoutRef.current = null;
        confirmingDeafRef.current = false;
        if (pingSentAtRef.current === null || wsRef.current !== ws) return;
        console.warn('[ClubPlannerWidget] WebSocket stopped answering pings; reconnecting');
        // Handled now as the drop it is (status, backoff, queued sends), and
        // detached by that: the browser's own close, whenever it comes, finds
        // the socket replaced and is ignored.
        ws.onclose?.call(ws, new CloseEvent('close', { code: 1006 }));
        try {
          ws.close(4000, 'No pong');
        } catch {
          // Already closing: it is detached either way.
        }
      }, DEAF_CONFIRM_MS);
      return;
    }

    sendPing();
    if (sentAt === null) {
      pingSentAtRef.current = Date.now();
      if (pongTimeoutRef.current) clearTimeout(pongTimeoutRef.current);
      pongTimeoutRef.current = setTimeout(() => {
        pongTimeoutRef.current = null;
        probeRef.current(ws);
      }, pingInterval);
    }
  }, [pingInterval]);

  /**
   * Disconnect from the WebSocket server.
   */
  const disconnect = useCallback(() => {
    isIntentionalCloseRef.current = true;
    clearTimers();
    reconnectAttemptsRef.current = 0;

    if (wsRef.current) {
      wsRef.current.close(1000, 'Client disconnect');
      wsRef.current = null;
    }

    setStatus('disconnected');
  }, [clearTimers]);

  /**
   * Send a client event to the server.
   */
  const send = useCallback((event: ClientEvent) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(event));
    } else {
      // Socket is down (reconnecting, backgrounded tab, navigation). Don't drop
      // the event — buffer it and deliver on the next open so a form submission
      // survives a transient disconnect instead of being silently lost
      // (CAIV-440). TOOL_RESPONSEs are also persisted so they survive a full
      // page reload.
      console.warn(
        '[ClubPlannerWidget] WebSocket not open; queued event for delivery on reconnect:',
        event.type
      );
      outboundQueueRef.current.push(event);
      persistPendingToolResponse(pendingStorageKey(channelId, routerId), event);
    }
  }, [channelId, routerId]);

  /**
   * Send a user message.
   */
  const sendMessage = useCallback(
    (message: string) => {
      send({
        type: ClientEventType.USER_MESSAGE,
        message,
      });
    },
    [send]
  );

  /**
   * Register an event handler for a specific event type.
   * Returns an unsubscribe function.
   */
  const on = useCallback(
    <T extends ServerEvent>(
      eventType: ServerEventTypeValue,
      handler: ServerEventHandler<T>
    ): (() => void) => {
      if (!handlersRef.current.has(eventType)) {
        handlersRef.current.set(eventType, new Set());
      }

      const handlers = handlersRef.current.get(eventType)!;
      handlers.add(handler as ServerEventHandler);

      // Return unsubscribe function
      return () => {
        handlers.delete(handler as ServerEventHandler);
        if (handlers.size === 0) {
          handlersRef.current.delete(eventType);
        }
      };
    },
    []
  );

  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  useEffect(() => {
    connectRef.current = connect;
    disconnectRef.current = disconnect;
    statusRef.current = status;
    probeRef.current = probe;
  }, [connect, disconnect, status, probe]);

  // Proactively reconnect when the tab returns to the foreground or the network
  // comes back. A backgrounded/suspended tab (common on mobile) freezes the
  // backoff `setTimeout`, so without this the socket can stay dead after the
  // user returns — and any form they then submit would be queued forever
  // (CAIV-440). Reconnecting here gets the queued submission delivered.
  useEffect(() => {
    const reconnectIfDropped = () => {
      if (isIntentionalCloseRef.current) return;
      // A terminal answer (4003/4004, or a socket the page refuses) stays
      // terminal: the same request gets the same answer (CAIV-1366).
      if (statusRef.current === 'unavailable') return;
      const ws = wsRef.current;
      // Open: a phone that slept or a network that switched can leave it open
      // and deaf, so ping now rather than at the next tick (CAIV-1366).
      if (ws && ws.readyState === WebSocket.OPEN) {
        probeRef.current(ws);
        return;
      }
      if (ws?.readyState === WebSocket.CONNECTING) return;
      // Throttle: a flapping network can fire `online` repeatedly; don't kick a
      // fresh connect more than once per MIN_RECONNECT_INTERVAL_MS so a dead
      // server isn't hammered (CAIV-440 review).
      const now = Date.now();
      if (now - lastReconnectKickRef.current < MIN_RECONNECT_INTERVAL_MS) return;
      lastReconnectKickRef.current = now;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      // The attempt count is kept: a kick that fails backs off as before
      // instead of starting a fresh burst of one-second retries.
      connectRef.current();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') reconnectIfDropped();
    };
    window.addEventListener('online', reconnectIfDropped);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('online', reconnectIfDropped);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  // Auto-connect on mount if enabled
  useEffect(() => {
    if (autoConnect) {
      connectRef.current();
    }

    return () => {
      disconnectRef.current();
    };
  }, [autoConnect]);

  // Reconnect when connection scope changes
  useEffect(() => {
    if (statusRef.current === 'connected') {
      disconnectRef.current();
      connectRef.current();
    }
  }, [wsHost, routerId, channelId]);

  return {
    status,
    isConnected: status === 'connected',
    send,
    sendMessage,
    connect,
    disconnect,
    on,
    error,
  };
}

export default useWebSocket;
